import type { ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { NavigateWithParams } from '../components/NavigateWithParams';
import { SingleQueryResults } from '../components/QueryResults/SingleQueryResults';
import { useActiveWorkspace } from '../hooks/useActiveWorkspace';
import { workspaceTypeIsAgent } from '../utils/helpers/common';

type RequireAgentForHistoricRoyaltiesProps = {
  children: ReactNode;
};

/**
 * Guards Historic Royalties -> Past Consolidated Statements: renders for agent
 * (BusinessAgency) workspaces, sends everyone else to the Past Statements tab, so
 * the tab is unreachable by URL as well as hidden from the nav. Named for its
 * route because the redirect target is fixed — a guard for somewhere else
 * wants its own component, not a `redirectTo` threaded through this one.
 *
 * {@link NavigateWithParams}, not `Navigate`: dropping `?v=2`/`?v=3` sends
 * `MigrationSwitch` to `oldComponent` (not-found) on a flag-off route.
 */
export const RequireAgentForHistoricRoyalties = ({
  children,
}: RequireAgentForHistoricRoyaltiesProps) => {
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const [, activeWorkspaceQuery] = useActiveWorkspace();

  const fallbackPath = `/workspace/${workspaceId!}/royalties/statements`;

  return (
    <SingleQueryResults query={activeWorkspaceQuery}>
      {(workspace) =>
        workspaceTypeIsAgent(workspace) ? (
          <>{children}</>
        ) : (
          <NavigateWithParams replace to={fallbackPath} />
        )
      }
    </SingleQueryResults>
  );
};
